import React from 'react';
import { Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import {
    READINESS_BAND_COLORS, RISK_BAND_COLORS, CONTEXT_BAND_COLORS, PARTICIPATION_BAND_COLORS,
} from '../../utils/constants';

/*
 * "How the COS Assessment Is Scored" — a common slide, kept as static front-end
 * text (Seongmin, 22 Sep: "manage it as static content on the front-end … the
 * slide shared by Ian serves as the definitive source"). Source: Ian's
 * "Dashboard Common Section — Introduction & Methodology" deck (16 Sep),
 * DB-CS-P0,P1-All-AssessmentRubric — p.16 (the four scoring rubrics) and p.17
 * (the risk rubric). Shared by the School and School Network dashboards; the
 * datahouse section this used to read (SD S_16) is no longer sent.
 */

const NAVY = '#052F44';

export const SCORING_RUBRICS = [
    {
        title: 'Children Online Safety Readiness',
        desc: 'Preparedness to act safely, responsibly, and effectively online.',
        icon: VerifiedUserIcon, color: '#1B6B55', colors: READINESS_BAND_COLORS,
        bands: [
            {name: 'High',     meaning: 'Strong, consistent readiness to participate online safely, responsibly, and effectively.'},
            {name: 'Moderate', meaning: 'Generally adequate readiness, with some areas for reinforcement.'},
            {name: 'Basic',    meaning: 'Partial or inconsistent readiness; targeted support may be needed.'},
            {name: 'Low',      meaning: 'Limited readiness or risky gaps requiring priority attention.'},
        ],
    },
    {
        title: 'Child Cyber-Risk Experiences',
        desc: 'Reported exposure to, involvement in, or impact from cyber risks.',
        icon: ReportProblemIcon, color: '#E04535', colors: RISK_BAND_COLORS,
        bands: [
            {name: 'Monitor',          meaning: 'Lower than the reference threshold or minimal cyber-risk signal'},
            {name: 'Watch',            meaning: 'Noticeable signal or within expected reference range'},
            {name: 'Elevated',         meaning: 'Above typical reference level or meaningful cyber-risk pattern'},
            {name: 'Priority Concern', meaning: 'Top-quartile reference level or high cyber-risk prevalence'},
        ],
    },
    {
        title: 'Context & Support',
        desc: 'Access, guidance, school education, home support, and help-seeking conditions.',
        icon: PeopleIcon, color: '#3B6FA0', colors: CONTEXT_BAND_COLORS,
        bands: [
            {name: 'Strong',   meaning: 'Strong access, support, guidance, or protective conditions.'},
            {name: 'Adequate', meaning: 'Generally positive conditions, with some minor limitations.'},
            {name: 'Limited',  meaning: 'Partial, inconsistent, or uncertain support conditions.'},
            {name: 'Low',      meaning: 'Weak, insufficient, or absent support conditions.'},
        ],
    },
    {
        title: 'Participation and Data Confidence',
        desc: 'Assessment coverage and the reliability of results for interpretation.',
        icon: StorageIcon, color: '#4B5563', colors: PARTICIPATION_BAND_COLORS,
        bands: [
            {name: 'High',     meaning: 'Strong enough for interpretation'},
            {name: 'Medium',   meaning: 'Usable with confidence label'},
            {name: 'Low',      meaning: 'Interpret cautiously'},
            {name: 'Very Low', meaning: 'Do not use for strong action or comparison'},
        ],
    },
];

const REF = 'Reference percentile';
const AI = 'Provisional AI-risk';
const AI_BANDS = ['0–4.9%', '5–14.9%', '15–29.9%', '≥30%'];
const pct = (a, b, c) => [`≤${a}%`, `>${a}–${b}%`, `>${b}–${c}%`, `>${c}%`];

export const RISK_RUBRIC = [
    {n: 1,  category: 'Headline',                    indicator: 'Any Cyber Risk',                               method: REF, bands: pct('71.95', '76.62', '80.09')},
    {n: 2,  category: 'Conduct Risk',                indicator: 'Cyberbullying',                                method: REF, bands: pct('26.29', '31.70', '34.71')},
    {n: 3,  category: 'Conduct Risk',                indicator: 'Cybervictimization',                           method: REF, bands: pct('32.73', '39.53', '44.86')},
    {n: 4,  category: 'Conduct Risk',                indicator: 'AI Image and Voice Misuse Victimization',      method: AI,  bands: AI_BANDS},
    {n: 5,  category: 'Content Risk',                indicator: 'Sexual Content Exposure',                      method: REF, bands: pct('20.67', '25.99', '31.02')},
    {n: 6,  category: 'Content Risk',                indicator: 'Violent Content Exposure',                     method: REF, bands: pct('15.75', '18.19', '20.38')},
    {n: 7,  category: 'Content Risk',                indicator: 'AI Misinformation and Deceptive Content Risk', method: AI,  bands: AI_BANDS},
    {n: 8,  category: 'Contact Risk',                indicator: 'Offline Meetings with Online Strangers',       method: REF, bands: pct('23.20', '26.30', '30.05')},
    {n: 9,  category: 'Contact Risk',                indicator: 'Sexual Conversation',                          method: REF, bands: pct('7.85', '9.43', '12.93')},
    {n: 10, category: 'Contact Risk',                indicator: 'Unwanted Online Sexual Contact',               method: REF, bands: pct('19.41', '27.59', '31.73')},
    {n: 11, category: 'Contact Risk',                indicator: 'AI-Generated Sexual Image Risk',               method: 'Provisional safeguarding-sensitive AI-risk', bands: ['0–0.9%', '1–4.9%', '5–9.9%', '≥10%']},
    {n: 12, category: 'Contract & Data Risk',        indicator: 'Misuse of Personal Information',               method: REF, bands: pct('19.19', '25.87', '30.57')},
    {n: 13, category: 'Contract & Data Risk',        indicator: 'Underage Technology Use',                      method: REF, bands: pct('39.54', '50.02', '58.02')},
    {n: 14, category: 'Wellbeing & Screen Use Risk', indicator: 'Excessive Screen Time',                        method: REF, bands: pct('47.92', '55.12', '60.71')},
    {n: 15, category: 'Wellbeing & Screen Use Risk', indicator: 'Video Game Addiction Risk',                    method: REF, bands: pct('8.23', '10.08', '13.33')},
    {n: 16, category: 'Wellbeing & Screen Use Risk', indicator: 'Online Technology Addiction Risk',             method: REF, bands: pct('34.39', '39.01', '49.01')},
    {n: 17, category: 'Wellbeing & Screen Use Risk', indicator: 'AI Emotional Reliance',                        method: AI,  bands: AI_BANDS},
];

export const RISK_RUBRIC_FOOTNOTE = '* For established risks, bands use risk-specific reference thresholds. For AI-related risks, provisional thresholds are used until enough reference data exists.';

const BandChip = ({text, color}) => (
    <Box sx={{
        display: 'inline-block', backgroundColor: color || '#6B7280', color: '#ffffff', fontSize: '12px', fontWeight: 700,
        px: 1.5, py: 0.5, borderRadius: '6px', whiteSpace: 'nowrap',
    }}>
        {text}
    </Box>
);

/** The four p.16 rubric cards, one per measure. */
export const ScoringBandCards = () => (
    <Grid container spacing={3} alignItems="stretch">
        {SCORING_RUBRICS.map(({title, desc, icon: Icon, color, colors, bands}) => (
            <Grid item xs={12} sm={6} md={3} key={title}>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '50%', backgroundColor: `${color}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <Icon style={{color, fontSize: 22}}/>
                        </Box>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{title}</Typography>
                    </Box>
                    <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6, mb: 2}}>{desc}</Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                        {bands.map((b) => (
                            <Box key={b.name}>
                                <Box sx={{mb: 0.6}}><BandChip text={b.name} color={colors[b.name]}/></Box>
                                <Typography sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5}}>{b.meaning}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Grid>
        ))}
    </Grid>
);

const headCell = {backgroundColor: NAVY, color: '#fff', fontSize: '13px', fontWeight: 700, borderBottom: 'none', py: 2, px: 2};
const bodyCell = {fontSize: '13px', color: '#374151', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6'};
const RISK_BAND_NAMES = ['Monitor', 'Watch', 'Elevated', 'Priority Concern'];

/** The p.17 risk rubric: all 17 risks with their band thresholds. */
export const RiskRubricTable = () => (
    <Box sx={{mt: 4}}>
        <Typography sx={{fontSize: '16px', fontWeight: 800, color: NAVY, mb: 2}}>Risk Rubric</Typography>
        <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{...headCell, width: '4%'}}>#</TableCell>
                        <TableCell sx={headCell}>Risk Category</TableCell>
                        <TableCell sx={headCell}>Risk indicator</TableCell>
                        <TableCell sx={headCell}>Banding method</TableCell>
                        {RISK_BAND_NAMES.map((b) => (
                            <TableCell key={b} sx={{...headCell, textAlign: 'center'}}>{b}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {RISK_RUBRIC.map((r, i) => (
                        <TableRow key={r.n} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #fff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                            <TableCell sx={bodyCell}>{r.n}</TableCell>
                            <TableCell sx={bodyCell}>{r.category}</TableCell>
                            <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{r.indicator}</TableCell>
                            <TableCell sx={bodyCell}>{r.method}</TableCell>
                            {r.bands.map((v, bi) => (
                                <TableCell key={RISK_BAND_NAMES[bi]} sx={{...bodyCell, textAlign: 'center', whiteSpace: 'nowrap'}}>{v}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 3, lineHeight: 1.6}}>
            {RISK_RUBRIC_FOOTNOTE}
        </Typography>
    </Box>
);
